import { useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface MultiImageUploadProps {
    value?: string[];
    onChange: (files: File[]) => void;
    onRemove: (index: number) => void;
    label?: string;
    maxImages?: number;
    disabled?: boolean;
}

export function MultiImageUpload({
    value = [],
    onChange,
    onRemove,
    label = "Images",
    maxImages = 5,
    disabled = false
}: MultiImageUploadProps) {
    const [isDragging, setIsDragging] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length + value.length > maxImages) {
            return;
        }
        onChange(files);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length + value.length > maxImages) {
            return;
        }
        onChange(files);
    };

    return (
        <div className="space-y-3">
            {label && <label className="text-sm font-medium">{label}</label>}

            {/* Image Grid */}
            {value.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                    {value.map((url, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border bg-muted">
                            <img
                                src={url}
                                alt={`Product ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                disabled={disabled}
                                className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            {index === 0 && (
                                <div className="absolute bottom-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-md">
                                    Main
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Area */}
            {value.length < maxImages && (
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    className={cn(
                        "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                        isDragging && "border-primary bg-primary/5",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                        Drag and drop images here, or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                        {value.length}/{maxImages} images uploaded
                    </p>
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        disabled={disabled}
                        className="hidden"
                        id="multi-image-upload"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        onClick={() => document.getElementById('multi-image-upload')?.click()}
                    >
                        Choose Files
                    </Button>
                </div>
            )}
        </div>
    );
}
